import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import './StaticPage.css';

const LAST_UPDATED = 'March 1, 2025';
const SITE_NAME = 'AstroEpanchang';
const SITE_URL = 'https://www.astroepanchang.com';

const sections = [
  {
    title: '1. Acceptance of Terms',
    content: `By accessing or using ${SITE_NAME} (${SITE_URL}), you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use the site.`,
  },
  {
    title: '2. Description of Service',
    content: `${SITE_NAME} provides free online Vedic astrology tools including horoscope generation, daily panchangam, zodiac sign information, marriage compatibility analysis, and related educational astrology content. The service is provided "as is" for informational and entertainment purposes only.`,
  },
  {
    title: '3. Disclaimer – Not Professional Advice',
    content: 'All astrological content on this site is for informational and entertainment purposes only. It does not constitute professional, medical, legal, financial, or psychological advice. Do not make major life decisions based solely on astrological information. Always consult qualified professionals for serious matters.',
  },
  {
    title: '4. User Responsibilities',
    items: [
      'You must be at least 16 years of age to use this service.',
      'You agree not to misuse the service, attempt unauthorised access, or interfere with the site\'s operation.',
      'You are responsible for the accuracy of data you enter (birth details, location, etc.).',
      'You agree not to scrape, reproduce, or redistribute site content without prior written permission.',
    ],
  },
  {
    title: '5. Intellectual Property',
    content: `All content on ${SITE_NAME} — including text, graphics, logos, calculations, and software — is the property of ${SITE_NAME} or its content suppliers and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without explicit written permission.`,
  },
  {
    title: '6. Third-Party Links & Services',
    content: 'The site may contain links to third-party websites or use third-party services (e.g., Google Analytics, Google AdSense). We are not responsible for the content, privacy practices, or availability of those external services. Accessing them is at your own risk.',
  },
  {
    title: '7. Accuracy of Information',
    content: 'While we strive to provide accurate Vedic astrological calculations based on established traditions, we make no warranties regarding the completeness, accuracy, or suitability of the information for any purpose. Astrological interpretations are traditional and subjective in nature.',
  },
  {
    title: '8. Limitation of Liability',
    content: `To the maximum extent permitted by law, ${SITE_NAME} shall not be liable for any direct, indirect, incidental, special, or consequential damages arising from your use of, or inability to use, the service — including but not limited to loss of data, revenue, or profits.`,
  },
  {
    title: '9. Indemnification',
    content: `You agree to indemnify and hold harmless ${SITE_NAME}, its operators, and affiliates from any claims, damages, or expenses (including legal fees) arising from your use of the service, violation of these Terms, or infringement of any third-party rights.`,
  },
  {
    title: '10. Account & Data',
    content: 'If you submit birth details or personal information through the site, you grant us a limited licence to process that data solely for the purpose of providing the requested astrological calculations. See our Privacy Policy for full details on data handling.',
  },
  {
    title: '11. Modifications to Terms',
    content: 'We reserve the right to update these Terms at any time. The "Last Updated" date will reflect the latest revision. Continued use of the site after changes constitutes acceptance of the updated Terms.',
  },
  {
    title: '12. Governing Law',
    content: 'These Terms shall be governed by and construed in accordance with applicable laws. Any disputes shall be subject to the exclusive jurisdiction of the competent courts.',
  },
];

const Terms = () => {
  const hydrated = useHydrated();
  return (
    <div className="static-page">
      <SEO
        title="Terms of Service"
        description={`Read the Terms of Service for ${SITE_NAME}. Learn about acceptable use, disclaimers, intellectual property, and your rights when using our Vedic astrology platform.`}
        keywords="terms of service, terms and conditions, astroepanchang terms"
        canonicalPath="/terms"
        noindex={false}
      />
      <div className="container">
        <motion.div
          className="static-page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>Terms of <span className="gradient-text">Service</span></h1>
          <p className="static-meta">Last updated: {LAST_UPDATED}</p>
          <p className="static-intro">
            Please read these Terms of Service carefully before using {SITE_NAME}.
            They govern your access to and use of our Vedic astrology platform.
          </p>
        </motion.div>

        <motion.div
          className="static-content"
          initial={hydrated ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          {sections.map((sec, i) => (
            <div key={i} className="static-section">
              <h2>{sec.title}</h2>
              {sec.content && <p>{sec.content}</p>}
              {sec.items && (
                <ul>
                  {sec.items.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Terms;
